import React from 'react'
import { Icon } from '../../atoms/Icon'
import { ScoreRadar } from '../../molecules/ScoreRadar'
import { getScoreColor } from '@popety-io/utils'
import {
  LandAnalysisCardProps,
  LandAnalysisCardTranslations,
} from './LandAnalysisCard.types'
import {
  StyledCard,
  CardHeader,
  RadarWrapper,
  AddressSection,
  Address,
  Municipality,
  MetricsRow,
  Metric,
  MetricIcon,
  MetricValue,
  CardFooter,
  EgridText,
  ViewLink,
  PreviousPlanBadge,
} from './LandAnalysisCard.styles'

const defaultTranslations: LandAnalysisCardTranslations = {
  today: 'Today',
  yesterday: 'Yesterday',
  daysAgo: (count: number) => `${count} ${count === 1 ? 'day' : 'days'} ago`,
  weeksAgo: (count: number) => `${count} ${count === 1 ? 'week' : 'weeks'} ago`,
  monthsAgo: (count: number) =>
    `${count} ${count === 1 ? 'month' : 'months'} ago`,
  yearsAgo: (count: number) => `${count} ${count === 1 ? 'year' : 'years'} ago`,
  sqm: 'm²',
  bldg: 'bldg',
  units: 'units',
  view: 'View Analysis',
  analyzed: 'Last analyzed',
  previousPlan: 'Previous plan',
}

const createFormatDate =
  (t: LandAnalysisCardTranslations) =>
  (date: Date | string): string => {
    const now = new Date()
    const analyzedDate = new Date(date)
    const diffInMs = now.getTime() - analyzedDate.getTime()
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24))

    if (diffInDays === 0) return t.today
    if (diffInDays === 1) return t.yesterday
    if (diffInDays < 7) return t.daysAgo(diffInDays)
    if (diffInDays < 30) return t.weeksAgo(Math.floor(diffInDays / 7))
    if (diffInDays < 365) return t.monthsAgo(Math.floor(diffInDays / 30))
    return t.yearsAgo(Math.floor(diffInDays / 365))
  }

const formatArea = (area: number): string => {
  return area.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
}

export const LandAnalysisCard: React.FC<LandAnalysisCardProps> = ({
  landAnalysis,
  onView,
  onHover,
  isHovered,
  isOutOfPlanWindow,
  className,
  translations,
}) => {
  const t = { ...defaultTranslations, ...translations }
  const formatDate = createFormatDate(t)
  const handleClick = (e: React.MouseEvent) => {
    // Allow middle-click and ctrl+click to open in new tab
    if (e.button === 1 || e.ctrlKey || e.metaKey) {
      return
    }
    e.preventDefault()
    if (onView) {
      onView(landAnalysis.id)
    }
  }

  const handleViewClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (onView) {
      onView(landAnalysis.id)
    }
  }

  const handleMouseEnter = () => {
    if (onHover) {
      onHover(landAnalysis.id)
    }
  }

  const handleMouseLeave = () => {
    if (onHover) {
      onHover(null)
    }
  }

  return (
    <StyledCard
      className={className}
      onClick={onView ? handleClick : undefined}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      $isHovered={isHovered}
      $clickable={!!onView}
      $isOutOfPlanWindow={isOutOfPlanWindow}
    >
      {isOutOfPlanWindow && (
        <PreviousPlanBadge>{t.previousPlan}</PreviousPlanBadge>
      )}
      <CardHeader>
        <RadarWrapper>
          <ScoreRadar
            property={landAnalysis}
            size={40}
            getScoreColor={getScoreColor}
          />
        </RadarWrapper>
        <AddressSection>
          <Address>{landAnalysis.address}</Address>
          <Municipality>
            {landAnalysis.municipality} - EGRID: {landAnalysis.egrid}
          </Municipality>
        </AddressSection>
      </CardHeader>

      <MetricsRow>
        <Metric>
          <MetricIcon>
            <Icon name="ruler" size="small" color="#808080" />
          </MetricIcon>
          <MetricValue>{formatArea(landAnalysis.area)}</MetricValue>
          <span>{t.sqm}</span>
        </Metric>
        <Metric>
          <MetricIcon>
            <Icon name="building" size="small" color="#808080" />
          </MetricIcon>
          <MetricValue>{landAnalysis.building_nb ?? 0}</MetricValue>
          <span>{t.bldg}</span>
        </Metric>
        <Metric>
          <MetricIcon>
            <Icon name="door-open" size="small" color="#808080" />
          </MetricIcon>
          <MetricValue>{landAnalysis.unit_nb ?? 0}</MetricValue>
          <span>{t.units}</span>
        </Metric>
      </MetricsRow>

      <CardFooter>
        {landAnalysis.lastAnalyzed && (
          <EgridText>
            {t.analyzed}: {formatDate(landAnalysis.lastAnalyzed)}
          </EgridText>
        )}
        {onView && (
          <ViewLink
            onClick={handleViewClick}
            aria-label="View land analysis details"
          >
            {t.view}
            <Icon name="arrow-right" size="small" />
          </ViewLink>
        )}
      </CardFooter>
    </StyledCard>
  )
}
